import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { request } from '../../Utils/request';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';
import OtpInput from 'react-otp-input';

const ForgotPasswordOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [recoveryMethod, setRecoveryMethod] = useState<'phone' | 'email'>('phone');
  const [resendLoading, setResendLoading] = useState(false);

  // Get phone number or email from location state
  useEffect(() => {
    const state = location.state as { phoneNumber?: string; email?: string; recoveryMethod?: 'phone' | 'email' };
    if (state?.phoneNumber) {
      setPhoneNumber(state.phoneNumber);
      setRecoveryMethod('phone');
    } else if (state?.email) {
      setEmail(state.email);
      setRecoveryMethod(state.recoveryMethod ?? 'email');
    } else {
      navigate('/forgot-password');
    }
  }, [location, navigate]);

  const handleOtpChange = (value: string) => {
    setOtp(value);
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const body = recoveryMethod === 'phone'
        ? { otp, phoneNumber }
        : { otp, email };
      const res = await request<any, { otp: string; phoneNumber?: string; email?: string }>('/auth/forgot-password/verify-otp', {
        method: 'POST',
        body,
      });

      if (res.message || res.success) {
        setSuccess(res.message || 'OTP verified successfully');
        setTimeout(() => {
          navigate('/reset-password', { 
            state: { 
              resetToken: res.resetToken || res.token || res.data?.resetToken || res.data?.token,
              phoneNumber: recoveryMethod === 'phone' ? phoneNumber : undefined,
              email: recoveryMethod === 'email' ? email : undefined,
              otpVerified: true 
            } 
          });
        }, 1500);
      }
    } catch (err: any) {
      setError(err.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const body = recoveryMethod === 'phone' ? { phoneNumber } : { email };
      const res = await request<any, { phoneNumber?: string; email?: string }>('/auth/forgot-password/resend-otp', {
        method: 'POST',
        body,
      });

      if (res.message) {
        setSuccess(recoveryMethod === 'phone' ? 'OTP resent successfully to your phone number' : 'OTP resent successfully to your email');
      } else {
        setSuccess('OTP resent successfully');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const displayPhoneNumber = phoneNumber 
    ? phoneNumber.replace(/^\+91(\d{2})(\d{4})(\d{4})$/, '+91 $1****$3') 
    : '';
  const displayEmail = email ? email.replace(/(.{2})(.*)(@.*)/, '$1***$3') : '';
  const sentToText = recoveryMethod === 'phone' ? displayPhoneNumber : displayEmail;

  return (
    <>
      <TopNavBar title="Verify OTP" canGoBack={true} />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#ffffff', minHeight: '100vh', padding: '16px' }}>
        <Box 
          style={{ width: '100%', maxWidth: 400, minWidth: 280 }}
        >
          {/* Heading */}
          <Typography variant="h5" align="center" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
            Enter Verification Code
          </Typography>

          {/* Instructional Text */}
          <Typography variant="subtitle1" align="center" sx={{ mb: 3, color: 'text.secondary' }}>
            We've sent a 6-digit verification code to {sentToText}
          </Typography>

          {/* OTP Input Form */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mb: 2,
              width: '100%',
              px: { xs: 1, sm: 2 },
            }}>
              <Box sx={{
                width: '100%',
                maxWidth: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}>
                <OtpInput
                  value={otp}
                  onChange={handleOtpChange}
                  numInputs={6}
                  renderSeparator={<span style={{ width: '4px' }} />}
                  renderInput={(props) => (
                    <input
                      {...props}
                      style={{
                        flex: '1 1 0',
                        minWidth: '40px',
                        maxWidth: '45px',
                        width: '100%',
                        height: '45px',
                        fontSize: '18px',
                        textAlign: 'center',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        outline: 'none',
                        boxSizing: 'border-box',
                        margin: '0',
                        padding: '0',
                      }}
                    />
                  )}
                  containerStyle={{
                    display: 'flex',
                    width: '100%',
                    gap: '4px',
                    justifyContent: 'space-between',
                  }}
                />
              </Box>
            </Box>

            <Button 
              type="submit" 
              variant="contained" 
              style={{ 
                marginTop: 8, 
                backgroundColor: '#FF6B35', 
                textTransform: 'none', 
                borderRadius: 8, 
                fontWeight: 600,
                padding: '12px 16px' 
              }} 
              fullWidth
              disabled={loading || otp.length !== 6}
            >
              {loading ? 'Verifying...' : 'Verify OTP'}
            </Button>
          </form>

          {/* Resend OTP Link */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'inline' }}>
              Didn't receive the code?{' '}
            </Typography>
            <Link
              component="button"
              variant="body2"
              onClick={handleResendOtp}
              disabled={resendLoading}
              sx={{
                color: '#FF6B35',
                textDecoration: 'none',
                fontWeight: 'bold',
                '&:hover': {
                  textDecoration: 'underline',
                },
                '&:disabled': {
                  color: '#ccc',
                }
              }}
            >
              {resendLoading ? 'Sending...' : 'Resend OTP'}
            </Link>
          </Box>

          {success && <Alert severity="success" style={{ marginTop: 16 }}>{success}</Alert>}
          {error && <Alert severity="error" style={{ marginTop: 16 }}>{error}</Alert>}
        </Box>
      </div>
    </>
  );
};

export default ForgotPasswordOTP;

